import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Users, Settings, ArrowRight, Clock, Tag } from 'lucide-react';
import { SearchResult } from '../types';

interface SearchResultsProps {
  results: SearchResult[];
  query: string;
  isLoading: boolean;
}

const SearchResults: React.FC<SearchResultsProps> = ({ results, query, isLoading }) => {
  const navigate = useNavigate();

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'page':
        return <FileText className="h-4 w-4" />;
      case 'section':
        return <Users className="h-4 w-4" />;
      case 'subsection':
        return <Settings className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'page':
        return 'bg-blue-100 text-blue-800';
      case 'section':
        return 'bg-green-100 text-green-800';
      case 'subsection':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'page':
        return 'Page';
      case 'section':
        return 'Section';
      case 'subsection':
        return 'Subsection';
      default:
        return 'Content';
    }
  };

  const highlightQuery = (text: string, query: string) => {
    if (!query) return text;

    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Searching...</span>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
        <p className="text-gray-600 mb-6">
          We couldn't find any documentation matching "{query}". Try adjusting your search terms.
        </p>
        <div className="space-y-2">
          <p className="text-sm text-gray-500">Suggestions:</p>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Check your spelling</li>
            <li>• Try more general keywords</li>
            <li>• Use fewer keywords</li>
            <li>• Try different keywords</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {results.map((result, index) => (
        <div
          key={result.id}
          onClick={() => navigate(result.path)}
          className="p-6 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-lg transition-all duration-200 cursor-pointer group bg-white"
        >
          <div className="flex items-start space-x-4">
            {/* Type Icon */}
            <div className={`w-10 h-10 ${getTypeColor(result.type)} rounded-lg flex items-center justify-center flex-shrink-0`}>
              {getTypeIcon(result.type)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-200 mb-2">
                    {highlightQuery(result.title, query)}
                  </h3>

                  {/* Meta Information */}
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(result.type)}`}>
                      {getTypeLabel(result.type)}
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      Recently updated
                    </span>
                    <span className="flex items-center">
                      <Tag className="h-3 w-3 mr-1" />
                      Documentation
                    </span>
                  </div>
                </div>

                {/* Arrow Icon */}
                <div className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <ArrowRight className="h-5 w-5 text-primary-600" />
                </div>
              </div>

              {/* Excerpt */}
              <p className="text-gray-600 mb-3 leading-relaxed">
                {highlightQuery(result.excerpt, query)}
              </p>

              {/* Path */}
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                  {result.path}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Results Summary */}
      <div className="text-center py-4 text-sm text-gray-500">
        Showing {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
      </div>
    </div>
  );
};

export default SearchResults;
